import { useState, useEffect, useCallback } from 'react'
import { Typography, Container, Paper, Box, Button, LinearProgress } from '@mui/material'

// Import All Form Sections
import PersonalDetails from './FormSections/PersonalDetails'
import AddressInformation from './FormSections/AddressInformation'
import ContactDetails from './FormSections/ContactDetails'
import InterpreterQuestion from './FormSections/InterpreterQuestion'
import EthnicityBackgroundInformation from './FormSections/EthnicityBackgroundInformation'
import DietaryAndAllergies from './FormSections/DietaryAndAllergies'
import HealthInsurance from './FormSections/HealthInsurance'
import BirthcareServiceSelection from './FormSections/BirthcareServiceSelection'
import LeadMaternityCarer from './FormSections/LeadMaternityCarer'
import EmergencyContact from './FormSections/EmergencyContact'
import TermsAndConditions from './FormSections/TermsAndConditions'
import SummaryPage from './FormSections/SummaryPage'
import DBError from './FormSections/DBError'

// List of Sections
const sections = [
  { name: 'PersonalDetails', component: PersonalDetails },
  { name: 'AddressInformation', component: AddressInformation },
  { name: 'ContactDetails', component: ContactDetails },
  { name: 'InterpreterQuestion', component: InterpreterQuestion },
  { name: 'EthnicityBackgroundInformation', component: EthnicityBackgroundInformation },
  { name: 'DietaryAndAllergies', component: DietaryAndAllergies },
  { name: 'HealthInsurance', component: HealthInsurance },
  { name: 'BirthcareServiceSelection', component: BirthcareServiceSelection },
  { name: 'LeadMaternityCare', component: LeadMaternityCarer },
  { name: 'EmergencyContact', component: EmergencyContact },
  { name: 'TermsAndConditions', component: TermsAndConditions },
  { name: 'SummaryPage', component: SummaryPage },
  { name: 'DBError', component: DBError }
]

function RegistrationForm () {
  const [step, setStep] = useState(0)
  const [isValid, setIsValid] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    nhiNumber: '',
    dob: '',
    countryOfBirth: '',
    occupation: '',
    address: '',
    suburb: '',
    city: '',
    postcode: '',
    address2: '',
    mobileNumber: '',
    alternativeNumber: '',
    email: '',
    interpreterNeeded: '',
    motherEthnicity1: '',
    motherEthnicity2: '',
    motherEthnicity3: '',
    babyEthnicity: '',
    specialDietaryNeeds: '',
    drugAllergies: '',
    hasInsurance: '',
    insuranceCompany: '',
    insuranceType: '',
    carerType: '',
    carerFirstName: '',
    carerSurname: '',
    postnatalMidwifeName: '',
    expectedDueDate: '',
    proposedPlaceOfBirth: '',
    gravida: '',
    parity: '',
    emergencyFirstName: '',
    emergencySurname: '',
    emergencyRelationship: '',
    emergencyMobileNumber: '',
    alternativeContactNumber: '',
    emergencyContactAddress: '',
    termsAndConditions: '',
    privacyStatement: '',
    isNZResident: '',
    birthcareOptions: 'postnatalOnly',
    roomOptions: '',
    additionalCommentsRequirements: '',
    scheduleOfCharges: ''
  })

  const isLastStep = step >= sections.length - 2
  const CurrentSection = sections[step]?.component

  const handleFormDataChange = (newData) => {
    setFormData(prevData => ({ ...prevData, ...newData }))
  }

  const prevStep = () => {
    setStep(prevStep => Math.max(prevStep - 1, 0))
  }

  const nextStep = useCallback(() => {
    // only proceed to next section if all fields are valid
    if (!isValid) return setShowErrors(true)
    setStep(prevStep => prevStep + 1)
    setShowErrors(false)
  }, [isValid])

  const handleSubmit = useCallback(async () => {
    if (!isValid) return setShowErrors(true)
    setShowErrors(false)

    try {
      // Send form data to the backend
      const response = await fetch('/submit-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()
      if (response.ok) {
        (window.dataLayer = window.dataLayer || []).push({ event: 'form_submit_success', formName: 'birthcare_reg_portal', responseTime: Date.now() })
        setSubmitted(true) // Show the thank you page
      } else {
        console.error('error:', data)
        console.error('Failed to submit form:', data.message)
        if (sections[step].name !== 'DBError') setStep(prevStep => prevStep + 1) // go to DBError page if failed
      }
    } catch (error) {
      console.error('Error:', error)
      if (sections[step].name !== 'DBError') setStep(prevStep => prevStep + 1) // go to DBError page if failed
    }
  }, [isValid, formData, step])

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') !isLastStep ? nextStep() : handleSubmit()
    }
    document.addEventListener('keydown', handleKeyPress)
    return () => { document.removeEventListener('keydown', handleKeyPress) }
  }, [isLastStep, nextStep, handleSubmit])

  // Thank You page after submitting form
  if (submitted) {
    return (
      <Container maxWidth='sm'>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant='h5' sx={{ mb: 2 }}>
            Thank you for filling out the form!
          </Typography>
          <Typography variant='body1'>
            We appreciate you taking the time to provide all the required details. Our team will review your submission and get back to you shortly.
          </Typography>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth='sm'>
      <Paper elevation={3} sx={{ width: '100%', height: 'auto', p: 4, borderRadius: 3 }}>
        {step < sections.length - 1 && (
          <LinearProgress
            variant='determinate'
            value={(step / (sections.length - 2)) * 100}
            sx={{ mb: 2, height: 10, borderRadius: 5, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { backgroundColor: '#285D96' } }}
          />
        )}

        <form>
          {CurrentSection && (
            <CurrentSection
              onValidationChange={setIsValid}
              showErrors={showErrors}
              formData={formData}
              onFormDataChange={handleFormDataChange}
            />
          )}

          <Box mt={2} display='flex' justifyContent='flex-end' gap={2}>
            {step > 0 && (
              <Button color='black' variant='outlined' onClick={prevStep} sx={{ borderRadius: 10, color: 'black' }}>
                Back
              </Button>
            )}
            <Button
              variant='contained'
              onClick={isLastStep ? handleSubmit : nextStep}
              sx={{ backgroundColor: '#285D96', color: '#fff', borderRadius: 10 }}
            >
              {isLastStep ? 'Submit' : 'Next'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  )
}

export default RegistrationForm
